import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isPreferenceConflict } from "../api/settings-client.js";
import { useAuthStore } from "../auth/auth-store.js";
import {
  composePresentationReplacement,
  effectiveTimeZone,
} from "./presentation-replacement.js";
import type { PresentationPrefs } from "./types.js";
import type { PresentationThemeStatus } from "./usePresentationTheme.js";
import {
  presentationPreferencesQueryKey,
  usePresentationPreferencesQuery,
  useUpdatePresentationPreferences,
} from "./useSettings.js";

/**
 * Region's instant time-zone write on the presentation preference (PB-5134,
 * d27/d29). Mirrors `usePresentationTheme`: the PUT is composed from the
 * FRESHEST cached row with only `timeZone` taken from the control, `status`
 * lets the control disable while there is no row to compose from, and a
 * compare-and-swap conflict is reported so the page can say the value was
 * updated elsewhere while the mutation's own error path refetches the row.
 */
export function usePresentationTimeZone(): {
  readonly timeZone: string;
  readonly persisted: boolean;
  readonly status: PresentationThemeStatus;
  /** A PUT is in flight for a row that did load (distinct from a pending read). */
  readonly saving: boolean;
  readonly conflict: boolean;
  readonly setTimeZone: (next: string) => void;
} {
  const preferences = usePresentationPreferencesQuery();
  const { mutate, isPending: isWriting } = useUpdatePresentationPreferences();
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);
  const [conflict, setConflict] = useState(false);

  const status: PresentationThemeStatus = preferences.data
    ? isWriting
      ? "pending"
      : "ready"
    : preferences.isPending
      ? "pending"
      : "unavailable";

  const setTimeZone = useCallback(
    (next: string) => {
      const current = queryClient.getQueryData<PresentationPrefs>(
        presentationPreferencesQueryKey(tenantId, userId),
      );
      if (!current || isWriting || !next || current.timeZone === next) return;
      setConflict(false);
      mutate(composePresentationReplacement(current, { timeZone: next }), {
        onError: (error) => setConflict(isPreferenceConflict(error)),
      });
    },
    [isWriting, mutate, queryClient, tenantId, userId],
  );

  return {
    timeZone: effectiveTimeZone(preferences.data),
    persisted: (preferences.data?.version ?? 0) >= 1,
    status,
    saving: Boolean(preferences.data) && isWriting,
    conflict,
    setTimeZone,
  };
}
